import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { X, Check, Navigation, Compass, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Fix for default marker icon in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const MODAL_Z = 1100;

const createCurrentLocationIcon = (size: number, locateTitle: string) =>
  L.divIcon({
    className: 'current-location-marker',
    html: `<div style="width:${size}px;height:${size}px;background:#16a34a;border:3px solid white;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.4);" title="${locateTitle.replace(/"/g, '&quot;')}"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });

async function reverseGeocode(lat: number, lng: number, acceptLanguage: string): Promise<string> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=${encodeURIComponent(acceptLanguage)}`,
      { headers: { 'Accept-Language': acceptLanguage } }
    );
    const data = await res.json();
    return data.display_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  } catch {
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  }
}

function radiusBounds(center: [number, number], radiusKm: number): L.LatLngBounds {
  const lat = center[0];
  const lng = center[1];
  const radiusM = radiusKm * 1000;
  const latOffset = radiusM / 111_320;
  const lngOffset = radiusM / (111_320 * Math.cos((lat * Math.PI) / 180));
  return L.latLngBounds(
    [lat - latOffset, lng - lngOffset],
    [lat + latOffset, lng + lngOffset]
  );
}

interface MapRadiusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (lat: number, lng: number, radius: number) => void;
  initialLat?: number;
  initialLng?: number;
  initialRadius?: number;
}

const JORDAN_CENTER = { lat: 31.95, lng: 35.91 };

const MapClickHandler = ({ onClick }: { onClick: (lat: number, lng: number) => void }) => {
  useMapEvents({
    click: (e) => {
      onClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

function FitRadiusView({ center, radiusKm }: { center: [number, number]; radiusKm: number }) {
  const map = useMap();

  useEffect(() => {
    const bounds = radiusBounds(center, radiusKm);
    map.fitBounds(bounds, { padding: [20, 20], maxZoom: 15, animate: false });
    const timers = [50, 200, 450].map((ms) =>
      globalThis.setTimeout(() => map.invalidateSize({ animate: false }), ms)
    );
    return () => timers.forEach((id) => globalThis.clearTimeout(id));
  }, [map, center, radiusKm]);

  return null;
}

function MapResizeWatcher({ active }: { active: boolean }) {
  const map = useMap();

  useEffect(() => {
    if (!active) return;
    const run = () => map.invalidateSize({ animate: false });
    run();
    const timers = [100, 300, 600].map((ms) => globalThis.setTimeout(run, ms));
    const container = map.getContainer();
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => run())
      : null;
    ro?.observe(container);
    return () => {
      timers.forEach((id) => globalThis.clearTimeout(id));
      ro?.disconnect();
    };
  }, [map, active]);

  return null;
}

const MapRadiusModal: React.FC<MapRadiusModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  initialLat,
  initialLng,
  initialRadius = 30
}) => {
  const { t, i18n } = useTranslation('common');
  const nominatimLang =
    i18n.language?.startsWith('ar') ? 'ar,en' : i18n.language?.startsWith('en') ? 'en,ar' : `${i18n.language},en`;
  const [center, setCenter] = useState<[number, number]>([initialLat || JORDAN_CENTER.lat, initialLng || JORDAN_CENTER.lng]);
  const [deviceLocation, setDeviceLocation] = useState<[number, number] | null>(null);
  const [address, setAddress] = useState<string>('');
  const [radius, setRadius] = useState(initialRadius);
  const [isLocating, setIsLocating] = useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const autoLocateOnceRef = useRef(false);

  const handleLocateMe = useCallback(() => {
    if (!navigator.geolocation) {
      globalThis.alert(t('map.geoNotSupported'));
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords: [number, number] = [pos.coords.latitude, pos.coords.longitude];
        setCenter(coords);
        setDeviceLocation(coords);
        setIsLocating(false);
      },
      () => {
        setIsLocating(false);
      }
    );
  }, [t]);

  useEffect(() => {
    setIsLoadingAddress(true);
    reverseGeocode(center[0], center[1], nominatimLang).then((res) => {
      setAddress(res);
      setIsLoadingAddress(false);
    });
  }, [center, nominatimLang]);

  useEffect(() => {
    if (!isOpen) {
      autoLocateOnceRef.current = false;
      return;
    }
    if (!initialLat && !initialLng && !autoLocateOnceRef.current) {
      autoLocateOnceRef.current = true;
      handleLocateMe();
    }
  }, [isOpen, initialLat, initialLng, handleLocateMe]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') return null;

  const locateTitle = t('map.locateMeMarkerTitle');

  const modal = (
    <div
      className="fixed inset-0 flex items-end justify-center bg-black/60 p-3 animate-fadeIn sm:items-center sm:p-4"
      style={{ zIndex: MODAL_Z }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="map-radius-modal-title"
    >
      <div className="animate-sheetUp flex max-h-[92dvh] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-900 sm:max-w-2xl lg:max-w-4xl">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-100 px-3 py-2.5 dark:border-slate-800 sm:p-6">
          <div className="min-w-0">
            <h2
              id="map-radius-modal-title"
              className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white sm:text-xl"
            >
              <span className="shrink-0 rounded-lg bg-blue-100 p-1.5 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400 sm:p-2">
                <Navigation size={16} className="sm:h-5 sm:w-5" />
              </span>
              <span className="truncate">{t('map.title')}</span>
            </h2>
            <p className="mt-0.5 line-clamp-1 text-[11px] text-slate-500 dark:text-slate-400 sm:text-sm">
              {t('map.subtitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 sm:p-2"
            aria-label={t('map.cancel')}
          >
            <X size={20} />
          </button>
        </div>

        {/* Map — ارتفاع مضبوط حتى تبقى النافذة مدمجة على الموبايل والتابلت */}
        <div className="relative h-[32dvh] min-h-[180px] max-h-[260px] shrink-0 overflow-hidden border-y border-slate-100 dark:border-slate-800 sm:h-[320px] sm:max-h-none lg:h-[400px]">
          <MapContainer
            key="map-radius-open"
            center={center}
            zoom={12}
            className="absolute inset-0 z-0 h-full w-full"
            zoomControl
          >
            <FitRadiusView center={center} radiusKm={radius} />
            <MapResizeWatcher active={isOpen} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapClickHandler onClick={(lat, lng) => setCenter([lat, lng])} />

            {deviceLocation && (
              <Marker position={deviceLocation} icon={createCurrentLocationIcon(24, locateTitle)} />
            )}

            <Marker position={center} />

            <Circle
              center={center}
              radius={radius * 1000}
              pathOptions={{ fillColor: '#3b82f6', color: '#1d4ed8', fillOpacity: 0.15 }}
            />
          </MapContainer>

          <button
            type="button"
            onClick={handleLocateMe}
            disabled={isLocating}
            className="absolute bottom-3 end-3 z-[500] flex items-center justify-center rounded-xl border border-slate-100 bg-white p-2.5 text-blue-600 shadow-lg transition-all hover:scale-105 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-blue-400 sm:bottom-6 sm:end-6 sm:rounded-2xl sm:p-3"
            title={t('map.locateMe')}
          >
            <Compass size={20} className={`sm:h-6 sm:w-6 ${isLocating ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Footer — قابل للتمرير على الموبايل إذا طال المحتوى */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain border-t border-slate-100 bg-slate-50/50 px-3 py-2.5 dark:border-slate-800 dark:bg-slate-900/50 sm:flex-none sm:overflow-visible sm:p-6 sm:pb-[max(env(safe-area-inset-bottom,0px),0.75rem)]">
          <div className="flex flex-col gap-2.5 sm:gap-6 md:flex-row md:items-center">
            <div className="w-full min-w-0 flex-1">
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 sm:text-sm">
                  {t('map.radiusLabel')}:
                </span>
                <span className="text-xs font-bold text-blue-600 dark:text-blue-400 sm:text-sm">
                  {radius} {t('map.unitKm')}
                </span>
              </div>
              <input
                type="range"
                min="1"
                max="200"
                step="5"
                value={radius}
                onChange={(e) => setRadius(parseInt(e.target.value, 10))}
                className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-slate-200 accent-blue-600 dark:bg-slate-700"
              />
              <div className="mt-1.5 rounded-lg border border-blue-100/50 bg-blue-50/50 p-2 dark:border-blue-800/30 dark:bg-blue-900/20 sm:mt-3 sm:rounded-xl sm:p-3">
                <p className="mb-0.5 flex items-center gap-1 text-[10px] font-bold text-slate-500 dark:text-slate-400 sm:mb-1 sm:text-[11px]">
                  <MapPin size={11} className="shrink-0 text-blue-500" />
                  {t('map.selectedAddress')}:
                </p>
                <p className="line-clamp-2 text-[11px] leading-snug text-slate-800 dark:text-slate-200 sm:line-clamp-2 sm:text-xs sm:leading-relaxed">
                  {isLoadingAddress ? (
                    <span className="flex animate-pulse items-center gap-2">
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-blue-400" />
                      {t('map.loadingAddress')}
                    </span>
                  ) : (
                    address
                  )}
                </p>
              </div>
              <div className="mt-1 hidden justify-between text-[10px] text-slate-400 sm:flex">
                <span>{t('map.slider1')}</span>
                <span>{t('map.slider50')}</span>
                <span>{t('map.slider100')}</span>
                <span>{t('map.slider150')}</span>
                <span>{t('map.slider200')}</span>
              </div>
            </div>

            <div className="flex w-full shrink-0 gap-2 pb-[max(env(safe-area-inset-bottom,0px),0.25rem)] sm:gap-3 md:w-auto md:pb-0">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-xl py-2.5 text-sm font-semibold text-slate-600 transition-all hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-800 sm:rounded-2xl sm:py-3 md:px-8"
              >
                {t('map.cancel')}
              </button>
              <button
                type="button"
                onClick={() => onConfirm(center[0], center[1], radius)}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-blue-600 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-200 transition-all hover:bg-blue-700 dark:shadow-none sm:gap-2 sm:rounded-2xl sm:py-3 md:px-10"
              >
                <Check size={18} />
                {t('map.confirm')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default MapRadiusModal;
